import { useEffect } from 'react'

/**
 * Scroll-linked depth for a section. Every `[data-parallax="<px>"]` descendant
 * drifts by up to that many pixels as the section crosses the viewport, so the
 * layers separate slightly instead of arriving as one flat block.
 *
 * Writes the `translate` property (via `--parallax-y`), leaving `transform`
 * free for the reveal transition and Tailwind's own translate utilities on
 * other elements. Nothing runs while the section is off-screen, and the whole
 * effect is skipped under prefers-reduced-motion.
 */
export function useParallax(ref) {
  useEffect(() => {
    const root = ref.current
    if (!root) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const targets = Array.from(root.querySelectorAll('[data-parallax]'))
    if (!targets.length) return

    let ticking = false
    let onScreen = false

    function update() {
      ticking = false
      const rect = root.getBoundingClientRect()
      // 0 as the section enters the bottom of the viewport, 1 as it leaves the top.
      const raw = (window.innerHeight - rect.top) / (window.innerHeight + rect.height)
      const offset = Math.min(1, Math.max(0, raw)) - 0.5
      for (const target of targets) {
        const distance = Number(target.dataset.parallax) || 0
        target.style.setProperty('--parallax-y', `${(-offset * distance).toFixed(1)}px`)
      }
    }

    function onScroll() {
      if (!onScreen || ticking) return
      ticking = true
      requestAnimationFrame(update)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting
        if (onScreen) update()
      },
      { rootMargin: '15% 0px' },
    )
    observer.observe(root)

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    update()

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      targets.forEach((target) => target.style.removeProperty('--parallax-y'))
    }
  }, [ref])
}
